// Phase JA.4 — WinCelebration overlay.
//
// Triggered ONLY when a locked slip's `outcome` transitions to "won"
// (detected by the parent via the realtime-fed useUserSlips hook). The
// component itself is a pure renderer; "no fabrication" lives at the
// parent: do not pass a slip in unless its DB row says outcome='won'.
//
// Doctrine carry-forward:
//   - No fabrication: the WAT-time, leg-count, and odds are read from
//     the slip row.
//   - No primitive edits: composes Badge (amber/success), Eyebrow,
//     Stat, Pill only.

import type { CSSProperties } from "react";
import { Badge, Eyebrow, Pill, Stat } from "../super-acca";
import type { UserSlip } from "../../free-tier/useUserSlips.ts";

const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";
const fontStat = "var(--sa-font-stat)";

export interface WinCelebrationProps {
  /** The won slip. When null, the component renders nothing. */
  slip: UserSlip | null;
  onDismiss: () => void;
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 90,
  padding: 24,
};

const cardStyle: CSSProperties = {
  width: "100%",
  maxWidth: 480,
  background: "var(--sa-bg-panel)",
  border: "1px solid var(--sa-accent-saffron)",
  borderRadius: 10,
  padding: "28px 26px",
  display: "flex",
  flexDirection: "column",
  gap: 18,
  boxShadow: "0 0 0 1px var(--sa-amber-glow), 0 24px 60px rgba(0,0,0,0.55)",
};

function fmtSettledWAT(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Africa/Lagos",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

export function WinCelebration({ slip, onDismiss }: WinCelebrationProps): JSX.Element | null {
  if (!slip) return null;
  if (slip.outcome !== "won") return null;

  const odds = slip.combined_odds ?? null;
  const settled = fmtSettledWAT(slip.settled_at ?? slip.locked_at);
  const ret = slip.potential_return;

  return (
    <div role="dialog" aria-label="Slip won" style={overlayStyle} data-testid="win-celebration">
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Eyebrow>Slip Settled</Eyebrow>
          <Badge variant="success">SETTLED</Badge>
        </div>
        <div
          style={{
            fontFamily: fontStat,
            fontSize: 36,
            lineHeight: 1.05,
            color: "var(--sa-accent-saffron)",
            letterSpacing: "0.04em",
          }}
        >
          SLIP WON
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 14,
          }}
        >
          <Stat label="Legs" value={String(slip.legs.length)} />
          <Stat label="Odds" value={odds !== null ? `${odds.toFixed(2)}×` : "—"} accent />
          <Stat label="Return" value={ret !== null ? ret.toFixed(2) : "—"} />
        </div>
        {settled ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Pill>{`Settled ${settled} WAT`}</Pill>
          </div>
        ) : null}
        <p
          style={{
            margin: 0,
            fontFamily: fontBody,
            fontSize: 13,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          STRIKZA never places bets on your behalf. This is a record of a slip you logged and the
          DB-confirmed outcome.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onDismiss}
            data-testid="win-celebration-dismiss"
            style={{
              background: "var(--sa-accent-saffron)",
              border: "1px solid var(--sa-accent-saffron)",
              color: "var(--sa-bg-base)",
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: "0.22em",
              padding: "10px 18px",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            CONTINUE
          </button>
        </div>
      </div>
    </div>
  );
}
